import { useState, useEffect, useRef } from "react";

const LINKEDIN_URL = "https://www.linkedin.com/in/bradleymassey";
const TIDU_URL     = "https://tidu.ai";
const BANDIK_URL   = "https://bandik.ai";
const EMAIL        = "info@masbro.be";

const BANDIK_FEATURES = [
  {
    head: "Environment-specific decoys",
    body: "Honeypots are built to mirror the services characteristic of your sector. A software company gets exposed CI/CD infrastructure. A financial institution gets an unauthenticated message queue. An insurer gets a database that looks like it shouldn't be there. The decoy fits the environment.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  },
  {
    head: "AI-generated user activity",
    body: "Each honeypot maintains a continuous baseline of realistic activity — logins, file access, queries, job runs. When an attacker engages, the platform adapts its responses in real time to sustain the illusion and maximise dwell time inside the decoy.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 8v4l3 3"/></svg>,
  },
  {
    head: "Zero false positives",
    body: "Legitimate users have no reason to interact with infrastructure they don't know exists. Every alert is a confirmed signal. There is no tuning, no threshold management, and no noise — only real intrusion attempts.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L4 6v6c0 5.25 3.5 9.74 8 11 4.5-1.26 8-5.75 8-11V6l-8-4z"/><polyline points="9 12 11 14 15 10"/></svg>,
  },
  {
    head: "Session replay and telemetry",
    body: "Every attacker interaction is captured in full. The dashboard presents session replays, connection metadata, credential attempts, and command sequences — a detailed record of what was done, from where, and how.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>,
  },
];

const ISO_OFFERINGS = [
  {
    label: "ISMS Implementation",
    desc: "For organisations building an information security management system from scratch. We scope the ISMS correctly, establish a risk assessment methodology that fits how the business operates, and produce documentation that reflects actual practice.",
    deliverables: ["Context and scope definition", "Risk assessment methodology", "Statement of Applicability", "Control documentation", "Annex A implementation support"],
  },
  {
    label: "Gap Analysis and Audit Readiness",
    desc: "For organisations approaching certification or recertification. We assess conformity against ISO/IEC 27001:2022, work through gaps systematically, and prepare an evidence package before the external auditor arrives.",
    deliverables: ["Clause 4 to 10 conformity review", "All 93 Annex A controls assessed", "Prioritised remediation plan", "Evidence package preparation", "Pre-audit internal review"],
  },
  {
    label: "Internal Audit and Surveillance",
    desc: "For certified organisations that need ongoing assurance. We conduct internal audits to ISO 27001 requirements, identify nonconformities early, and support management review and surveillance cycle preparation.",
    deliverables: ["Annual internal audit programme", "Audit report and nonconformity tracking", "Management review support", "Surveillance cycle preparation", "Continual improvement guidance"],
  },
];

const TIDU_FEATURES = [
  {
    head: "Risk assessments",
    body: "The platform generates contextualised risk assessments based on the organisation's scope, assets, and threat profile. Output is structured for direct use in an ISO 27001 risk treatment process.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 6v6l4 2"/></svg>,
  },
  {
    head: "Control selection",
    body: "Controls are mapped across ISO 27001 Annex A and NIST 800-53 based on identified risks. Treatment plans are generated with ownership assignment and implementation guidance.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  },
  {
    head: "Residual risk scoring",
    body: "After treatment, the platform calculates residual risk and flags items that remain above tolerance thresholds. Leadership gets a current view of the organisation's risk posture.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  },
  {
    head: "Multi-framework support",
    body: "ISO/IEC 27001:2022, ISO/IEC 42001:2023, ISO 9001:2015, NIST 800-53, NIS-2, and the EU AI Act are all natively modelled. Control crosswalks reduce duplication for organisations working across more than one framework.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
];

/* ─── HOOKS ─────────────────────────────────────────────────── */
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return { isMobile: w < 640, isDesktop: w >= 1024 };
}

function useInView(t = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); io.disconnect(); } },
      { threshold: t }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [t]);
  return [ref, vis];
}

/* ─── SHARED NAV ────────────────────────────────────────────── */
function Nav({ page, setPage, menuOpen, setMenuOpen }) {
  const [scrolled, setScrolled] = useState(false);
  const { isDesktop } = useBreakpoint();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => { if (isDesktop) setMenuOpen(false); }, [isDesktop, setMenuOpen]);

  const navTo = (p, id) => {
    setPage(p);
    setMenuOpen(false);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 80);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const SOLUTIONS = [
    { label: "Information Security Consulting", page: "home",   id: "iso27001" },
    { label: "Enterprise Risk Management",      page: "home",   id: "tidiai" },
    { label: "Honeypot-as-a-Service",           page: "bandik", id: null },
  ];

  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Close on Escape, return focus to trigger
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && dropOpen) { setDropOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [dropOpen]);

  return (
    <header style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 300,
      height: "60px", display: "flex", alignItems: "center",
      padding: "0 1.5rem 0 0", justifyContent: "space-between",
      background: scrolled ? "rgba(255,255,255,0.96)" : "#fff",
      borderBottom: `1px solid ${scrolled ? "var(--border)" : "#f1f5f9"}`,
      backdropFilter: scrolled ? "blur(14px)" : "none",
      transition: "all 0.28s",
    }}>
      {/* Wordmark */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.01rem", cursor: "pointer" }} onClick={() => navTo("home")}>
        <img src={`${process.env.PUBLIC_URL}/logo.svg`} width="122" height="122" alt="MASBRO" style={{ marginRight: "-35px" }} />
        <div style={{ fontFamily: "var(--sans)", fontSize: "0.92rem", fontWeight: 700, color: "var(--blue)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>MASBRO</div>
      </div>

      {/* Desktop nav */}
      {isDesktop && (
        <nav style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
          <span className="nav-link" onClick={() => navTo("about")}>About</span>
          {/* Solutions dropdown */}
          <div ref={dropRef} style={{ position: "relative" }}>
            <span ref={triggerRef} className="nav-link" onClick={() => setDropOpen(o => !o)}
              tabIndex={0} onKeyDown={e => e.key === "Enter" && setDropOpen(o => !o)}
              style={{ display: "flex", alignItems: "center", gap: "0.3rem", userSelect: "none", color: dropOpen ? "var(--blue)" : undefined }}>
              Solutions
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                style={{ transition: "transform 0.2s", transform: dropOpen ? "rotate(180deg)" : "none" }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </span>
            {dropOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 12px)", right: 0,
                minWidth: "max-content", background: "#fff", border: "1px solid var(--border)",
                borderRadius: "8px", boxShadow: "0 8px 28px rgba(0,0,0,0.09)",
                padding: "0.4rem", animation: "fadeUp 0.18s ease both", zIndex: 400,
              }}>
                {SOLUTIONS.map(s => (
                  <div key={s.id} className="dropdown-item" onClick={() => { navTo(s.page, s.id); setDropOpen(false); }}>
                    <div style={{ fontFamily: "var(--sans)", fontSize: "0.83rem", fontWeight: 600, color: "var(--ink)", marginBottom: "0.1rem" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button className="btn btn-primary" style={{ fontSize: "0.78rem", padding: "0.58rem 1.3rem" }} onClick={() => navTo("home", "contact")}>Get in touch</button>
        </nav>
      )}

      {/* Hamburger */}
      {!isDesktop && (
        <div className="burger" onClick={() => setMenuOpen(o => !o)}>
          <span style={{ transform: menuOpen ? "rotate(45deg) translate(5px,5px)" : "none" }}/>
          <span style={{ opacity: menuOpen ? 0 : 1 }}/>
          <span style={{ transform: menuOpen ? "rotate(-45deg) translate(5px,-5px)" : "none" }}/>
        </div>
      )}
    </header>
  );
}

/* ─── MOBILE MENU ───────────────────────────────────────────── */
function MobileMenu({ open, setPage, setMenuOpen }) {
  if (!open) return null;
  const go = (p, id) => {
    setPage(p);
    setMenuOpen(false);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 80);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 290, background: "rgba(15,23,42,0.4)", backdropFilter: "blur(4px)" }} onClick={() => setMenuOpen(false)}>
      <div style={{ position: "absolute", top: "60px", left: 0, right: 0, background: "#fff", borderBottom: "1px solid var(--border)", padding: "0.75rem 1.5rem 1.5rem", animation: "fadeUp 0.2s ease both" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: "var(--sans)", fontSize: "0.7rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", padding: "0.75rem 0 0.5rem" }}>Solutions</div>
        {[
          { label: "Information Security Consulting", page: "home",   id: "iso27001" },
          { label: "Enterprise Risk Management",      page: "home",   id: "tidiai" },
          { label: "Honeypot-as-a-Service",           page: "bandik", id: null },
        ].map(s => (
          <div key={s.id} onClick={() => go(s.page, s.id)}
            style={{ fontFamily: "var(--sans)", fontSize: "0.92rem", fontWeight: 500, color: "var(--ink)", padding: "0.75rem 0", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}>
            {s.label}
          </div>
        ))}
        <div onClick={() => go("about")} style={{ fontFamily: "var(--sans)", fontSize: "0.92rem", fontWeight: 500, color: "var(--slate)", padding: "0.75rem 0", borderBottom: "1px solid #f1f5f9", cursor: "pointer", marginTop: "0.5rem" }}>About</div>
        <button className="btn btn-primary" style={{ width: "100%", marginTop: "1.1rem", padding: "0.85rem" }} onClick={() => go("home", "contact")}>Get in touch</button>
      </div>
    </div>
  );
}

/* ─── CONTACT FORM PANEL ────────────────────────────────────── */
function ContactPanel() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.name || !form.email || !form.message) return;
    const body = `Name: ${form.name}%0ASubject: ${form.subject}%0A%0A${form.message}`;
    window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent(form.subject || "Enquiry from masbro.be")}&body=${body}`;
    setSent(true);
  };

  const inputStyle = {
    fontFamily: "var(--sans)", fontSize: "0.85rem", color: "var(--ink)",
    border: "1px solid var(--border)", borderRadius: "6px",
    padding: "0.65rem 0.85rem", width: "100%", outline: "none",
    transition: "border-color 0.18s", background: "#fff",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <h2 style={{ fontFamily: "var(--sans)", fontSize: "1.35rem", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.02em", marginBottom: "0.25rem" }}>Talk to us</h2>
      <p style={{ fontFamily: "var(--sans)", fontSize: "0.85rem", color: "var(--slate)", lineHeight: 1.65, marginBottom: "0.5rem" }}>
        Request a demonstration, ask about a specific standard, or tell us about your organisation's requirements. We will get back to you promptly.
      </p>

      {sent ? (
        <div style={{ padding: "1.5rem", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", textAlign: "center" }}>
          <div style={{ fontFamily: "var(--sans)", fontSize: "0.92rem", fontWeight: 600, color: "#15803d", marginBottom: "0.3rem" }}>Message prepared</div>
          <div style={{ fontFamily: "var(--sans)", fontSize: "0.82rem", color: "#166534" }}>Your email client should have opened. Send from there to complete.</div>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.65rem" }}>
            <input placeholder="Name *" value={form.name} onChange={e => set("name", e.target.value)} style={inputStyle}
              onFocus={e => e.target.style.borderColor = "var(--blue)"} onBlur={e => e.target.style.borderColor = "var(--border)"}/>
            <input placeholder="Email *" type="email" value={form.email} onChange={e => set("email", e.target.value)} style={inputStyle}
              onFocus={e => e.target.style.borderColor = "var(--blue)"} onBlur={e => e.target.style.borderColor = "var(--border)"}/>
          </div>
          <input placeholder="Subject" value={form.subject} onChange={e => set("subject", e.target.value)} style={inputStyle}
            onFocus={e => e.target.style.borderColor = "var(--blue)"} onBlur={e => e.target.style.borderColor = "var(--border)"}/>
          <textarea placeholder="Message *" rows={5} value={form.message} onChange={e => set("message", e.target.value)} style={{ ...inputStyle, resize: "vertical", minHeight: "110px" }}
            onFocus={e => e.target.style.borderColor = "var(--blue)"} onBlur={e => e.target.style.borderColor = "var(--border)"}/>
          <button className="btn btn-primary" style={{ padding: "0.8rem", fontSize: "0.82rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }} onClick={submit}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            Send message
          </button>
        </>
      )}
    </div>
  );
}

/* ─── FEATURE TILE ──────────────────────────────────────────── */
function FeatureTile({ item, i, light = false }) {
  const [ref, vis] = useInView();
  return (
    <div ref={ref} style={{
      display: "flex", gap: "1rem", padding: "1rem 1.1rem",
      background: light ? "rgba(255,255,255,0.08)" : "var(--mist)",
      border: `1px solid ${light ? "rgba(255,255,255,0.15)" : "var(--border)"}`,
      borderRadius: "8px",
      opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(14px)",
      transition: `opacity 0.45s ease ${i * 80}ms, transform 0.45s ease ${i * 80}ms`,
      color: light ? "rgba(255,255,255,0.9)" : "inherit",
    }}>
      <div style={{ width: "34px", height: "34px", borderRadius: "7px", background: light ? "rgba(255,255,255,0.15)" : "var(--blue-tint)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: light ? "#fff" : "var(--blue)" }}>{item.icon}</div>
      <div>
        <div style={{ fontFamily: "var(--sans)", fontSize: "0.84rem", fontWeight: 600, color: light ? "#fff" : "var(--ink)", marginBottom: "0.25rem" }}>{item.head}</div>
        <div style={{ fontFamily: "var(--sans)", fontSize: "0.8rem", color: light ? "rgba(255,255,255,0.72)" : "var(--slate)", lineHeight: 1.65 }}>{item.body}</div>
      </div>
    </div>
  );
}

/* ─── FOOTER ────────────────────────────────────────────────── */
function Footer({ isMobile }) {
  return (
    <footer style={{ background: "var(--ink)", padding: isMobile ? "1.5rem 1.25rem" : "1.6rem 2rem", display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", gap: "1rem" }}>
      <span style={{ fontFamily: "var(--sans)", fontSize: "0.76rem", color: "#64748b" }}>
        &copy; {new Date().getFullYear()} MASBRO SPRL, Grimbergen, Belgium
      </span>
      <div style={{ display: "flex", gap: isMobile ? "1.2rem" : "1.8rem", flexWrap: "wrap" }}>
        {[["tidu.ai", TIDU_URL], ["LinkedIn", LINKEDIN_URL], ["Privacy policy", "#"], ["Terms", "#"]].map(([l, href]) => (
          <span key={l} style={{ fontFamily: "var(--sans)", fontSize: "0.72rem", color: "#64748b", cursor: "pointer", transition: "color 0.15s" }}
            onClick={() => window.open(href, "_blank")}
            onMouseEnter={e => e.target.style.color = "#93c5fd"}
            onMouseLeave={e => e.target.style.color = "#64748b"}>{l}</span>
        ))}
      </div>
    </footer>
  );
}

/* ─── HOME PAGE ─────────────────────────────────────────────── */
function HomePage({ setPage }) {
  const [activeTab, setActiveTab] = useState(0);
  const { isMobile, isDesktop } = useBreakpoint();
  const col2 = isDesktop ? "1fr 1fr" : "1fr";

  // Enable scroll-snap on html for desktop only; removed on unmount or mobile
  useEffect(() => {
    if (isDesktop) document.documentElement.classList.add("snap-home");
    else document.documentElement.classList.remove("snap-home");
    return () => document.documentElement.classList.remove("snap-home");
  }, [isDesktop]);

  const snap = isDesktop ? { scrollSnapAlign: "start", scrollSnapStop: "always" } : {};

  return (
    <>
      {/* ══ HERO + CONTACT ══════════════════════════════════════ */}
      <div style={{ display: "flex", flexDirection: isDesktop ? "row" : "column", minHeight: "100vh", paddingTop: "60px", ...snap }}>

        {/* Left: blue panel */}
        <div style={{
          flex: isDesktop ? "0 0 55%" : "none",
          background: "var(--blue)",
          padding: isDesktop ? "4rem 4rem 3rem" : "2.5rem 1.5rem",
          display: "flex", flexDirection: "column", justifyContent: "center",
          position: "relative", overflow: "hidden",
        }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.06) 1px,transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" }}/>
          <div style={{ position: "relative", zIndex: 1, maxWidth: "520px" }}>
            <p style={{ fontFamily: "var(--mono)", fontSize: "0.6rem", letterSpacing: "0.18em", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", marginBottom: "1.5rem" }}>
              Information Security · Risk Management
            </p>
            <h1 style={{ fontFamily: "var(--sans)", fontSize: "clamp(1.8rem,3.5vw,2.8rem)", fontWeight: 700, color: "#fff", lineHeight: 1.12, letterSpacing: "-0.03em", marginBottom: "1.4rem" }}>
              Information Security done right.<br/>Compliance as a byproduct of risk management.
            </h1>
            <p style={{ fontFamily: "var(--sans)", fontSize: "0.92rem", color: "rgba(255,255,255,0.78)", lineHeight: 1.75, marginBottom: "2.2rem", maxWidth: "440px" }}>
              MASBRO is an IT security consultancy specialising in ISO/IEC 27001:2022 implementation and audit work. We build management systems that reflect how organisations operate.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginBottom: "2.2rem" }}>
              {[
                { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L4 6v6c0 5.25 3.5 9.74 8 11 4.5-1.26 8-5.75 8-11V6l-8-4z"/><polyline points="9 12 11 14 15 10"/></svg>, label: "ISO/IEC 27001:2022 Implementation and Audit" },
                { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>, label: "tidu.ai — AI-assisted Enterprise Risk Management" },
              ].map(({ icon, label }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
                  <div style={{ color: "rgba(255,255,255,0.7)", flexShrink: 0 }}>{icon}</div>
                  <span style={{ fontFamily: "var(--sans)", fontSize: "0.85rem", color: "rgba(255,255,255,0.85)" }}>{label}</span>
                </div>
              ))}
            </div>
            <p style={{ fontFamily: "var(--sans)", fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>
              ISO 27001 &nbsp;·&nbsp; ISO 9001 &nbsp;·&nbsp; NIST 800-53 &nbsp;·&nbsp; NIS-2 &nbsp;·&nbsp; EU AI Act
            </p>
          </div>
        </div>

        {/* Right: white contact panel */}
        <div id="contact" style={{
          flex: isDesktop ? "1" : "none",
          background: "#fff",
          padding: isDesktop ? "4rem 3.5rem" : "2.5rem 1.5rem",
          display: "flex", flexDirection: "column", justifyContent: "center",
          borderLeft: isDesktop ? "1px solid var(--border)" : "none",
          borderTop: !isDesktop ? "1px solid var(--border)" : "none",
          scrollMarginTop: "60px",
        }}>
          <ContactPanel />
        </div>
      </div>

      {/* ══ ISO 27001 ════════════════════════════════════════════ */}
      <section id="iso27001" style={{
        background: "var(--mist)",
        minHeight: "100vh",
        display: "flex", flexDirection: "column", justifyContent: "center",
        padding: isDesktop ? "60px 0" : "3.5rem 0",
        ...snap,
      }}>
        <div className="container">
          <div style={{ maxWidth: "600px", marginBottom: "2.5rem" }}>
            <h2 style={{ fontFamily: "var(--sans)", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 700, color: "var(--ink)", lineHeight: 1.18, letterSpacing: "-0.03em", marginBottom: "1rem" }}>
              Information Security Consulting
            </h2>
            <p style={{ fontFamily: "var(--sans)", fontSize: "0.92rem", color: "var(--slate)", lineHeight: 1.78 }}>
              ISO 27001 is worth pursuing when the process improves how the organisation manages information security risk. MASBRO approaches every engagement with that outcome in mind, whether the starting point is a blank page, an upcoming audit, or an ISMS that needs to hold up to scrutiny.
            </p>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", overflowX: "auto", paddingBottom: "4px", WebkitOverflowScrolling: "touch" }}>
            {ISO_OFFERINGS.map((o, i) => (
              <button key={i} className={`tab-btn${activeTab === i ? " active" : ""}`} onClick={() => setActiveTab(i)}>{o.label}</button>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr" : "1fr", gap: isDesktop ? "3rem" : "2rem", background: "#fff", border: "1px solid var(--border)", borderRadius: "8px", padding: isMobile ? "1.5rem" : "2rem", boxShadow: "0 1px 8px rgba(0,0,0,0.04)" }}>
            <div>
              <h3 style={{ fontFamily: "var(--sans)", fontSize: "1.05rem", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.02em", marginBottom: "0.9rem" }}>{ISO_OFFERINGS[activeTab].label}</h3>
              <p style={{ fontFamily: "var(--sans)", fontSize: "0.9rem", color: "var(--slate)", lineHeight: 1.78, marginBottom: "1.6rem" }}>{ISO_OFFERINGS[activeTab].desc}</p>
              <button className="btn btn-primary" onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>Discuss your situation</button>
            </div>
            <div style={{ marginTop: isDesktop ? 0 : "1.5rem", paddingTop: isDesktop ? 0 : "1.5rem", borderTop: isDesktop ? "none" : "1px solid var(--border)" }}>
              <p style={{ fontFamily: "var(--sans)", fontSize: "0.75rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "1rem" }}>Deliverables</p>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {ISO_OFFERINGS[activeTab].deliverables.map((d, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                    <span style={{ width: "18px", height: "18px", borderRadius: "50%", background: "var(--blue-tint)", color: "var(--blue)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.6rem", fontWeight: 700, flexShrink: 0, marginTop: "2px" }}>✓</span>
                    <span style={{ fontFamily: "var(--sans)", fontSize: "0.86rem", color: "var(--slate)", lineHeight: 1.5 }}>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p style={{ fontFamily: "var(--sans)", fontSize: "0.88rem", color: "var(--slate-light)", lineHeight: 1.7, maxWidth: "680px", marginTop: "2rem", borderLeft: "3px solid var(--border-mid)", paddingLeft: "1.2rem" }}>
            ISO 27001 certification is most useful as the documented outcome of a risk management process the organisation has actually committed to. Treating it as the goal rather than the result tends to produce systems that work on paper and nowhere else.
          </p>
        </div>
      </section>

      {/* ══ TIDU.AI ══════════════════════════════════════════════ */}
      <section id="tidiai" style={{
        background: "var(--blue)",
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        position: "relative", overflow: "hidden",
        ...snap,
      }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.05) 1px,transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" }}/>

        {/* Centered content, grows above footer */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", justifyContent: "center",
          position: "relative", zIndex: 1,
          padding: isDesktop ? "60px 0 2rem" : "3.5rem 0",
        }}>
          <div className="container">
            <div style={{ display: "grid", gridTemplateColumns: col2, gap: isDesktop ? "4.5rem" : "2.5rem", alignItems: "start" }}>
              <div>
                <h2 style={{ fontFamily: "var(--sans)", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 700, color: "#fff", lineHeight: 1.18, letterSpacing: "-0.03em", marginBottom: "0.5rem" }}>
                  <a href={TIDU_URL} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }}>tidu.ai</a>
                </h2>
                <p style={{ fontFamily: "var(--sans)", fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", marginBottom: "1.2rem" }}>
                  Enterprise risk management platform, currently in beta.
                </p>
                <div style={{ width: "36px", height: "3px", background: "rgba(255,255,255,0.4)", borderRadius: "2px", marginBottom: "1.4rem" }}/>
                <p style={{ fontFamily: "var(--sans)", fontSize: "0.92rem", color: "rgba(255,255,255,0.8)", lineHeight: 1.78, marginBottom: "1rem" }}>
                  Most organisations manage risk in spreadsheets. Risk registers fall out of date, control evidence is scattered, and the link between identified risks and implemented controls is difficult to trace. <a href={TIDU_URL} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>tidu.ai</a> is built to address that.
                </p>
                <p style={{ fontFamily: "var(--sans)", fontSize: "0.92rem", color: "rgba(255,255,255,0.8)", lineHeight: 1.78, marginBottom: "1rem" }}>
                  The platform uses private hosted AI-models to generate risk assessments, recommend controls, support treatment decisions, and calculate residual risk, across ISO/IEC 27001:2022, ISO/IEC 42001:2023 ISO 9001:2015, NIST 800-53, NIS-2, and the EU AI Act.
                </p>
                <p style={{ fontFamily: "var(--sans)", fontSize: "0.84rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.7, marginBottom: "2rem" }}>
                  A limited number of organisations are currently being onboarded. Contact us to request a demonstration.
                </p>
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  <button className="btn" style={{ background: "#fff", color: "var(--blue)", padding: "0.72rem 1.6rem", fontWeight: 600 }} onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>Request a demo</button>
                  <button className="btn" style={{ background: "transparent", color: "rgba(255,255,255,0.8)", border: "1.5px solid rgba(255,255,255,0.3)", padding: "0.68rem 1.6rem" }} onClick={() => window.open(TIDU_URL, "_blank")}>Visit tidu.ai</button>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: isDesktop ? 0 : "2rem" }}>
                {TIDU_FEATURES.map((item, i) => <FeatureTile key={i} item={item} i={i} light />)}
              </div>
            </div>
          </div>
        </div>

        {/* Footer anchored to bottom of this snap section */}
        <Footer isMobile={isMobile} />
      </section>
    </>
  );
}

/* ─── ABOUT PAGE ────────────────────────────────────────────── */
function AboutPage({ setPage }) {
  const { isMobile, isDesktop } = useBreakpoint();
  const col2 = isDesktop ? "1fr 1fr" : "1fr";
  const sectionPad = isMobile ? "3rem 0" : "5rem 0";

  return (
    <div style={{ paddingTop: "60px" }}>
      {/* Header band */}
      <div style={{ background: "var(--blue)", padding: isMobile ? "3rem 0 2.5rem" : "4.5rem 0 4rem", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.06) 1px,transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" }}/>
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <p style={{ fontFamily: "var(--mono)", fontSize: "0.6rem", letterSpacing: "0.18em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", marginBottom: "1rem" }}>About MASBRO</p>
          <h1 style={{ fontFamily: "var(--sans)", fontSize: "clamp(1.8rem,4vw,3rem)", fontWeight: 700, color: "#fff", letterSpacing: "-0.03em", lineHeight: 1.1, maxWidth: "600px" }}>
            Independent. Experienced. Accountable.
          </h1>
        </div>
      </div>

      {/* Main content */}
      <section style={{ background: "#fff", padding: sectionPad }}>
        <div className="container">
          <div style={{ display: "grid", gridTemplateColumns: col2, gap: isDesktop ? "5rem" : "2.5rem", alignItems: "start" }}>
            <div>
              <div style={{ width: "36px", height: "3px", background: "var(--blue)", borderRadius: "2px", marginBottom: "1.6rem" }}/>
              <p style={{ fontFamily: "var(--sans)", fontSize: "0.95rem", color: "var(--slate)", lineHeight: 1.82, marginBottom: "1.1rem" }}>
                MASBRO SPRL is an IT security consultancy based in Grimbergen, founded in 2024. The practice focuses on ISO/IEC 27001:2022 implementation and audit consulting, and the development of <a href={TIDU_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--blue)" }}>tidu.ai</a>, an AI-assisted enterprise risk management platform, and <a href="https://bandik.ai" target="_blank" rel="noopener noreferrer" style={{ color: "var(--blue)" }}>bandik.ai</a>, honeypots as a service.
              </p>
              <p style={{ fontFamily: "var(--sans)", fontSize: "0.95rem", color: "var(--slate)", lineHeight: 1.82, marginBottom: "1.1rem" }}>
                Our clients are organisations working toward certification, maintaining an existing Information Security Management System, or looking for an internal audit programme they can rely on year to year.
              </p>
              <p style={{ fontFamily: "var(--sans)", fontSize: "0.95rem", color: "var(--slate)", lineHeight: 1.82, marginBottom: "2rem" }}>
                Engagements are handled by experienced consultants with a background in DevOps, information security governance, risk management, and ISO/IEC 27001:2022 audit.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <button className="btn btn-primary" onClick={() => { setPage("home"); setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 80); }}>Get in touch</button>
                <button className="btn btn-ghost" onClick={() => window.open(LINKEDIN_URL, "_blank")}>LinkedIn</button>
              </div>
            </div>

            {/* Profile card */}
            <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: "8px", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
              <div style={{ background: "var(--blue)", padding: "1.2rem 1.4rem" }}>
                <div style={{ fontFamily: "var(--sans)", fontSize: "0.9rem", fontWeight: 700, color: "#fff" }}>MASBRO SPRL</div>
                <div style={{ fontFamily: "var(--sans)", fontSize: "0.75rem", color: "#93c5fd", marginTop: "2px" }}>Company details</div>
              </div>
              {[
                ["Legal form",   "BV / SPRL"],
                ["Founded",      "2024"],
                ["Location",     "Grimbergen, Belgium"],
                ["Services",     "ISO 27001 consulting"],
                ["Platform",     <><a href={TIDU_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--blue)" }}>tidu.ai</a>{" (beta)"}</>],
                ["Frameworks",   "ISO 27001, ISO 9001, NIST 800-53, NIS-2, EU AI Act"],
                ["Availability", "Open to new mandates"],
              ].map(([k, v], i, arr) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.72rem 1.4rem", borderBottom: i < arr.length - 1 ? "1px solid #f1f5f9" : "none", gap: "1rem", background: i % 2 === 0 ? "#fff" : "var(--mist)" }}>
                  <span style={{ fontFamily: "var(--sans)", fontSize: "0.75rem", color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>{k}</span>
                  <span style={{ fontFamily: "var(--sans)", fontSize: "0.82rem", color: k === "Availability" ? "#16a34a" : "var(--slate)", fontWeight: k === "Availability" ? 600 : 400, textAlign: "right" }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer isMobile={isMobile} />
    </div>
  );
}

/* ─── BANDIK PAGE ───────────────────────────────────────────── */
function BandikPage({ setPage }) {
  const { isMobile, isDesktop } = useBreakpoint();
  const col2 = isDesktop ? "1fr 1fr" : "1fr";

  return (
    <div style={{ paddingTop: "60px" }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section style={{
        background: "var(--ink)",
        minHeight: isDesktop ? "100vh" : "auto",
        display: "flex", flexDirection: "column", justifyContent: "center",
        padding: isMobile ? "3.5rem 0" : "6rem 0",
        position: "relative", overflow: "hidden",
      }}>
        {/* Subtle grid */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" }}/>
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: col2, gap: isDesktop ? "5rem" : "2.5rem", alignItems: "center" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.5rem" }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: "0.58rem", letterSpacing: "0.18em", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>Pre-release</span>
                <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "#f59e0b", flexShrink: 0 }}/>
                <span style={{ fontFamily: "var(--mono)", fontSize: "0.58rem", letterSpacing: "0.18em", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>Honeypot-as-a-Service</span>
              </div>
              <h1 style={{ fontFamily: "var(--sans)", fontSize: "clamp(1.8rem,3.5vw,2.8rem)", fontWeight: 700, color: "#fff", lineHeight: 1.12, letterSpacing: "-0.03em", marginBottom: "1.4rem" }}>
                Deception infrastructure that looks exactly like the real thing.
              </h1>
              <p style={{ fontFamily: "var(--sans)", fontSize: "0.95rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.78, marginBottom: "1rem", maxWidth: "480px" }}>
                <a href={BANDIK_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#93c5fd", textDecoration: "none", fontWeight: 600 }}>bandik.ai</a> deploys AI-powered honeypots tailored to your environment — services that an attacker would expect to find, behaving exactly as they should. Until they do something they shouldn't.
              </p>
              <p style={{ fontFamily: "var(--sans)", fontSize: "0.95rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.78, marginBottom: "2.2rem", maxWidth: "480px" }}>
                Each decoy runs as a real service in an isolated container. The platform generates continuous, plausible user activity to make the environment convincing, and adapts in real time when an attacker engages.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <button className="btn btn-primary" onClick={() => { setPage("home"); setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 80); }}>
                  Register interest
                </button>
                <button className="btn" style={{ background: "transparent", color: "rgba(255,255,255,0.7)", border: "1.5px solid rgba(255,255,255,0.2)", padding: "0.68rem 1.6rem" }} onClick={() => window.open(BANDIK_URL, "_blank")}>
                  Visit bandik.ai
                </button>
              </div>
            </div>

            {/* Right: feature tiles */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: isDesktop ? 0 : "2.5rem" }}>
              {BANDIK_FEATURES.map((item, i) => (
                <div key={i} style={{
                  display: "flex", gap: "1rem", padding: "1rem 1.1rem",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "8px",
                }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "7px", background: "rgba(251,191,36,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#fbbf24" }}>{item.icon}</div>
                  <div>
                    <div style={{ fontFamily: "var(--sans)", fontSize: "0.84rem", fontWeight: 600, color: "#fff", marginBottom: "0.25rem" }}>{item.head}</div>
                    <div style={{ fontFamily: "var(--sans)", fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.65 }}>{item.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Pre-release notice ────────────────────────────────── */}
      <section style={{ background: "#fff", padding: isMobile ? "3rem 0" : "4.5rem 0", borderTop: "1px solid var(--border)" }}>
        <div className="container">
          <div style={{ maxWidth: "640px", margin: "0 auto", textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "20px", padding: "0.35rem 0.9rem", marginBottom: "1.8rem" }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#f59e0b", flexShrink: 0 }}/>
              <span style={{ fontFamily: "var(--mono)", fontSize: "0.6rem", letterSpacing: "0.12em", color: "#92400e", textTransform: "uppercase" }}>Pre-release — limited early access</span>
            </div>
            <h2 style={{ fontFamily: "var(--sans)", fontSize: "clamp(1.4rem,3vw,2rem)", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.03em", lineHeight: 1.2, marginBottom: "1.1rem" }}>
              Join the early access programme
            </h2>
            <p style={{ fontFamily: "var(--sans)", fontSize: "0.95rem", color: "var(--slate)", lineHeight: 1.78, marginBottom: "2rem" }}>
              bandik.ai is currently in pre-release. A limited number of organisations are being onboarded into the early access programme. If you are interested in deploying deception infrastructure ahead of general availability, get in touch.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button className="btn btn-primary" style={{ padding: "0.8rem 2rem" }} onClick={() => { setPage("home"); setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 80); }}>
                Register interest
              </button>
              <button className="btn btn-ghost" onClick={() => window.open(BANDIK_URL, "_blank")}>
                Visit bandik.ai
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer isMobile={isMobile} />
    </div>
  );
}

/* ─── ROOT ──────────────────────────────────────────────────── */
export default function App() {
  const [page, setPage] = useState("home");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  return (
    <div style={{ background: "#fff", color: "#0f172a", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        :root {
          --sans:        'Sora', sans-serif;
          --mono:        'IBM Plex Mono', monospace;
          --blue:        #2563eb;
          --blue-dark:   #1d4ed8;
          --blue-tint:   #eff6ff;
          --blue-mid:    #bfdbfe;
          --ink:         #0f172a;
          --slate:       #475569;
          --slate-light: #64748b;
          --mist:        #f8fafd;
          --border:      #e2e8f0;
          --border-mid:  #cbd5e1;
        }
        * { margin:0; padding:0; box-sizing:border-box; }
        html { scroll-behavior:smooth; }
        @media (min-width: 1024px) {
          html.snap-home { scroll-snap-type: y mandatory; }
        }
        body { -webkit-font-smoothing:antialiased; }
        ::-webkit-scrollbar { width:5px; background:#f8fafd; }
        ::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:3px; }
        ::selection { background:#dbeafe; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:none; } }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }

        .container { max-width:1060px; margin:0 auto; padding:0 1.25rem; }
        @media(min-width:640px) { .container { padding:0 2rem; } }

        .nav-link { font-family:var(--sans); font-size:0.82rem; font-weight:500; color:var(--slate-light); cursor:pointer; transition:color 0.15s; }
        .nav-link:hover { color:var(--blue); }

        .btn { cursor:pointer; font-family:var(--sans); font-size:0.8rem; font-weight:600; border-radius:6px; transition:all 0.18s; border:none; }
        .btn-primary { background:var(--blue); color:#fff; padding:0.72rem 1.6rem; }
        .btn-primary:hover { background:var(--blue-dark); }
        .btn-outline { background:#fff; color:var(--blue); border:1.5px solid var(--blue); padding:0.68rem 1.6rem; }
        .btn-outline:hover { background:var(--blue-tint); }
        .btn-ghost { background:transparent; color:var(--slate); border:1.5px solid var(--border-mid); padding:0.68rem 1.6rem; }
        .btn-ghost:hover { border-color:var(--blue); color:var(--blue); }

        .tab-btn { font-family:var(--sans); font-size:0.82rem; font-weight:500; cursor:pointer; padding:0.6rem 1.1rem; border-radius:6px; border:1.5px solid var(--border); background:#fff; color:var(--slate); transition:all 0.18s; white-space:nowrap; }
        .tab-btn.active { background:var(--blue); color:#fff; border-color:var(--blue); }
        .tab-btn:not(.active):hover { border-color:var(--blue); color:var(--blue); }

        .dropdown-item { display:flex; align-items:flex-start; gap:0.75rem; padding:0.8rem 0.9rem; border-radius:6px; cursor:pointer; transition:background 0.15s; }
        .dropdown-item:hover { background:var(--blue-tint); }

        .burger { display:flex; flex-direction:column; gap:5px; cursor:pointer; padding:4px; }
        .burger span { display:block; width:22px; height:2px; background:var(--ink); border-radius:2px; transition:all 0.2s; }

        input, textarea { font-family:var(--sans); }
        input::placeholder, textarea::placeholder { color:#94a3b8; }
      `}</style>

      <Nav page={page} setPage={setPage} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <MobileMenu open={menuOpen} setPage={setPage} setMenuOpen={setMenuOpen} />

      {page === "home"   && <HomePage   setPage={setPage} />}
      {page === "about"  && <AboutPage  setPage={setPage} />}
      {page === "bandik" && <BandikPage setPage={setPage} />}
    </div>
  );
}
